import { useMemo } from 'react';
import { ROLE_TYPES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { useMdmKillSwitch } from '@/hooks/useMdmKillSwitch';
import { usePermissions } from '@/hooks/usePermissions';
import { useRootCreateLock } from '@/contexts/RootActionLockContext';
import { useAppVariant } from '@/lib/app-variant';
import { NAV_TARGETS, type NavTarget } from '../utils/nav-targets';

/**
 * Filters `NAV_TARGETS` down to the entries this user is actually allowed to
 * see. The pure `matchNavTargets` matcher operates on this filtered list, so
 * a white-label admin who types "refer" gets zero "Refer & Earn" hits, not
 * a broken link.
 *
 * Gates evaluated (see `NavGate`):
 *   - `feature`               → company plan/feature flag
 *   - `minRole`               → role floor (admin/superAdmin/owner)
 *   - `restrictedRolesHidden` → hide for READ_ONLY / CONTENT_PROPOSAL /
 *                                CONTENT_APPROVAL (mirrors RestrictedRoute)
 *   - `whiteLabelHidden`      → hide when `brandingData.isWhiteLabel`
 *   - `brandingRemovedHidden` → hide when `brandingData.removeOptiSignsBranding`
 *   - `brandingFlag`          → per-page hide toggle on brandingData
 *   - `signageOnly`           → hide on OptiEdge variant
 *   - `childAccountHidden`    → hide on child sub-accounts
 *   - `defaultTeamOnly`       → hide when active team isn't the default
 *   - `nonDefaultTeamOnly`    → hide when active team IS the default
 *   - `canCreate`             → hide when `permissions.canCreate(entity)` is
 *                                false (mirrors destination toolbar's create-
 *                                write gate exactly)
 */
export function useVisibleNavTargets(targets: NavTarget[] = NAV_TARGETS): NavTarget[] {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const features = useCompanyFeatures();
  const permissions = usePermissions();
  // Every quick-create here lands at the section ROOT (the app selector / feeds dialog are seeded
  // with `currentPath: '/'`, the playlist / schedule dialogs open on the bare management route), and
  // no content page provider is mounted under the search box — so the Root create lock (§12 home
  // path, #2138; All Teamspaces scope, #2208) must be read here directly.
  const { locked: rootLocked } = useRootCreateLock('/');
  const variant = useAppVariant();
  const isDefaultTeam = useTeamStore((s) => s.isDefaultTeam);
  const mdmKillSwitchOn = useMdmKillSwitch();

  return useMemo(() => {
    const branding = me?.brandingData ?? company?.brandingData;
    const isWhiteLabel = !!branding?.isWhiteLabel;
    const removeOptiSignsBranding = !!branding?.removeOptiSignsBranding;
    const isOptiedge = variant === 'optiedge';
    // Child-account = has a parent link (sub-account under another company).
    // Mirrors `SettingsLayout`'s local `isChildAccount` derivation.
    const isChildAccount = !!company?.parentAccountId || !!company?.linkedAccountId;
    // Match `RestrictedRoute.tsx` exactly — the three roles that get
    // redirected away from restricted pages.
    const isRestrictedRole =
      permissions.teamRole === ROLE_TYPES.READ_ONLY ||
      permissions.teamRole === ROLE_TYPES.CONTENT_PROPOSAL ||
      permissions.teamRole === ROLE_TYPES.CONTENT_APPROVAL;

    return targets.filter((target) => {
      const gate = target.gate;
      if (!gate) return true;

      if (gate.signageOnly && isOptiedge) return false;
      if (gate.whiteLabelHidden && isWhiteLabel) return false;
      if (gate.brandingRemovedHidden && removeOptiSignsBranding) return false;

      if (gate.brandingFlag && branding?.[gate.brandingFlag]) return false;

      if (gate.feature) {
        if (gate.feature === 'mdm') {
          // Mirror `MdmFeatureRoute`: pass if `company.feature.mdm`
          // OR the global `mdmKillSwitchOn` is on.
          if (!company?.feature?.mdm && !mdmKillSwitchOn) return false;
        } else if (!features[gate.feature]) {
          return false;
        }
      }

      if (gate.minRole) {
        if (gate.minRole === 'owner' && !permissions.isRootOwner) return false;
        if (gate.minRole === 'superAdmin' && !permissions.isRootSuperAdmin) return false;
        if (gate.minRole === 'rootAdmin' && !permissions.isRootAdmin) return false;
        // `admin` = admin on the ACTIVE team — mirrors AccountMembersPage's
        // `isTeamAdminFor(activeTeamId)`: `isRootAdmin` shortcuts to true
        // (root admins have de-facto admin rights on every team, even ones
        // they aren't explicitly listed on in `me.teams`), otherwise the
        // team-role must be in ADMIN_ROLES = {OWNER, SUPER_ADMIN, ADMIN,
        // TEAM_OWNER} which is what `permissions.isTeamAdmin` checks.
        if (gate.minRole === 'admin' && !permissions.isTeamAdmin && !permissions.isRootAdmin)
          return false;
      }

      if (gate.restrictedRolesHidden && isRestrictedRole) return false;
      if (gate.childAccountHidden && isChildAccount) return false;
      if (gate.defaultTeamOnly && !isDefaultTeam) return false;
      if (gate.nonDefaultTeamOnly && isDefaultTeam) return false;

      // Role-only canCreate check (no folder context) — mirrors the create-
      // write toolbar buttons on destination pages. `usePermissions.canCreate`
      // returns true for admins + USER + CONTENT_APPROVAL universally; the
      // per-entity variance is CONTENT_PROPOSAL (only asset) and READ_ONLY
      // (nothing). See design doc §"Role × canCreate matrix".
      if (gate.canCreate && (rootLocked || !permissions.canCreate(gate.canCreate))) return false;

      return true;
    });
  }, [
    targets,
    me,
    company,
    features,
    permissions,
    variant,
    isDefaultTeam,
    mdmKillSwitchOn,
    rootLocked,
  ]);
}
